/**
 * Private error logger — writes to Supabase `error_logs` table.
 *
 * Design:
 * - Dev is completely off (import.meta.env.DEV → no-op) so local errors don't
 *   pollute the production table.
 * - Fire-and-forget: insert is dispatched via `void`; callers never await.
 * - Silent failure: any error thrown by the insert itself is swallowed —
 *   logging must never throw, or an error in error-reporting will re-enter
 *   the global error handlers and create an infinite loop.
 * - `user_id` is pulled from the current Supabase session; anonymous → null.
 * - 90-day retention runs server-side via pg_cron (`purge_old_error_logs`).
 */

import { supabase } from '@/integrations/supabase/client';

type ErrorKind = 'boundary' | 'unhandledrejection' | 'window.error';

export interface ErrorContext {
  componentStack?: string;
  kind?: ErrorKind;
  /** Optional boundary label; prepended to the stored message for grouping. */
  scope?: string;
}

/** Safe wrapper — no-op in dev, fire-and-forget in prod, never throws. */
export function captureException(error: unknown, context?: ErrorContext): void {
  if (import.meta.env.DEV) return;
  void insertLog(error, context).catch(() => {
    // logging must never throw; swallowing prevents an error-reporting loop
  });
}

async function insertLog(error: unknown, ctx?: ErrorContext): Promise<void> {
  try {
    const err = error instanceof Error ? error : new Error(String(error));
    const { data: { user } } = await supabase.auth.getUser();
    const scopedMessage = ctx?.scope ? `[${ctx.scope}] ${err.message}` : err.message;

    await supabase.from('error_logs').insert({
      message: scopedMessage.slice(0, 2000),
      stack: err.stack?.slice(0, 8000) ?? null,
      component_stack: ctx?.componentStack?.slice(0, 8000) ?? null,
      url: typeof window !== 'undefined' ? window.location.href : null,
      user_id: user?.id ?? null,
      kind: ctx?.kind ?? null,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    });
  } catch {
    // silent — see captureException comment
  }
}
